import {
  SET_NAVVIS_API_TOKEN,
  SET_SPLIT_INDOORVIEWER,
  SET_CLICKED_ITEM_TYPE,
  SET_CURRENT_LEVEL_IV_FRAME1,
  SET_CURRENT_LEVEL_IV_FRAME2,
  SET_ROUTE,
  RESET_ROUTE,
  CHANGE_PRIMARY_INSTANCE,
  CHANGE_SECONDARY_INSTANCE,
  SET_IV_LEVELS_FOR_FRAME,
  SET_IVION_LINKS,
  SET_PRIMARY_INSTANCE_POSITION,
  SET_PRIMARY_INSTANCE_ORIENTATION
} from '../mutation-types'
import {
  CHANGE_LEVEL,
  CHANGE_LEVEL_IV,
  CHANGE_LEVEL_IV_FRAME2,
  INIT_CONTEXTMENU,
  LOAD_IV_COMPONENTS,
  RESET_IVION_INSTANCES,
  SET_IV_LEVELS
} from '../action-types'
import { useIndoorviewStore } from '@/pinia/indoorview'
import iconMinus from '@/assets/minus.svg'

async function pollForFloors(indoorviewStore, frame) {
  const read = () =>
    frame === 1
      ? indoorviewStore.ivApi?.view.floorChangerService?.currentFloors
      : indoorviewStore.ivApi2?.view.floorChangerService?.currentFloors
  let floors = read()
  for (let attempts = 0; !floors && attempts < 10; attempts++) {
    await new Promise((resolve) => setTimeout(resolve, 200))
    floors = read()
  }
  return floors ?? null
}

export default {
  namespaced: true,
  state: {
    ivLevelsFrame1: null,
    ivLevelsFrame2: null,
    navvisApiToken: '',
    poiTypes: [],
    hasIvLoaded: false,
    route: null,
    numberOfInstances: 0,
    instanceLinks: [],
    primaryInstancePosition: null,
    primaryInstanceOrientation: null,
    primaryInstance: null,
    secondaryInstance: null,
    selectedLevelIvFrame1: null,
    selectedLevelIvFrame2: null,
    splitIndoorViewer: false
  },
  getters: {
    isSplitIndoorViewer(state) {
      return state.splitIndoorViewer
    },
    navvisInstanceURL(state) {
      return state.primaryInstance != null ? state.instanceLinks[state.primaryInstance].link : null
    },
    navvisInstanceURL2(state) {
      return state.secondaryInstance != null
        ? state.instanceLinks[state.secondaryInstance].link
        : null
    }
  },
  mutations: {
    [SET_IV_LEVELS_FOR_FRAME](state, payload) {
      if (payload.frame == 1) state.ivLevelsFrame1 = payload.floors
      if (payload.frame == 2) state.ivLevelsFrame2 = payload.floors
    },
    [SET_PRIMARY_INSTANCE_ORIENTATION](state, payload) {
      state.primaryInstanceOrientation = payload
    },
    [SET_PRIMARY_INSTANCE_POSITION](state, payload) {
      state.primaryInstancePosition = payload
    },
    [SET_IVION_LINKS](state, payload) {
      const links = []
      payload.forEach((linkObj) => {
        links.push({ link: linkObj.link, title: linkObj.title ? linkObj.title : null })
      })
      state.instanceLinks = links
      state.primaryInstance = state.instanceLinks.length ? state.instanceLinks.length - 1 : null
      state.secondaryInstance = state.primaryInstance
    },
    // Takes a payload to reset selected instance: Null
    [CHANGE_PRIMARY_INSTANCE](state, instanceNumber) {
      // check if instanceNumber is zero needed for url otherwise zero becomes falsy
      state.primaryInstance = instanceNumber >= 0 ? instanceNumber : null
    },
    [CHANGE_SECONDARY_INSTANCE](state, instanceNumber) {
      state.secondaryInstance = instanceNumber >= 0 ? instanceNumber : null
    },
    // Takes a payload with token: String
    [SET_NAVVIS_API_TOKEN](state, payload) {
      state.navvisApiToken = payload.token
    },
    [SET_CURRENT_LEVEL_IV_FRAME1](state, payload) {
      state.selectedLevelIvFrame1 = payload.levelNumber
    },
    [SET_CURRENT_LEVEL_IV_FRAME2](state, payload) {
      state.selectedLevelIvFrame2 = payload.levelNumber
    },
    // Takes a payload with splitIndoorViewer: Boolean
    [SET_SPLIT_INDOORVIEWER](state, payload) {
      state.splitIndoorViewer = payload.splitIndoorViewer
    },
    [SET_ROUTE](state, payload) {
      // Cannot store entire reactive route object, is causing app breakdown
      let instructions = payload.route.instructions.map((obj) => {
        return {
          text: obj.text,
          type: obj.type,
          floor: obj.floor.name,
          icon: obj.icons ? obj.icons.text : ''
        }
      })
      let routeLength = payload.route.routeLength
      state.route = { instructions: instructions, length: routeLength }
    },
    [RESET_ROUTE](state) {
      state.route = null
    },
    setPoiTypesVisibility(state, types) {
      state.poiTypes = types
    },
    setIvLoaded(state, isLoaded) {
      state.hasIvLoaded = isLoaded
    }
  },
  actions: {
    // Takes a payload levelNumber: String, the level of the selected floor and  fromMap: boolean, if request not sent from iv module.
    [CHANGE_LEVEL_IV]({ commit, dispatch, rootState }, payload) {
      const indoorviewStore = useIndoorviewStore()
      commit(SET_CURRENT_LEVEL_IV_FRAME1, { levelNumber: payload.levelNumber })
      if (indoorviewStore.ivApi?.view.floorChangerService) {
        indoorviewStore.ivApi.view.floorChangerService
          .changeFloorByName(payload.levelNumber)
          .then(() => {})
          .catch(() => {})
      }
      if (!Object.keys(rootState.mapview.selectedBuilding).length > 0) return // Floor picker hidden for real estate
      dispatch(CHANGE_LEVEL, { levelNumber: payload.levelNumber }, { root: true })
      commit(SET_CLICKED_ITEM_TYPE, { type: 'level' }, { root: true })
    },
    [CHANGE_LEVEL_IV_FRAME2]({ commit }, payload) {
      const indoorviewStore = useIndoorviewStore()
      commit(SET_CURRENT_LEVEL_IV_FRAME2, payload)
      if (indoorviewStore.ivApi2?.view.floorChangerService) {
        indoorviewStore.ivApi2.view.floorChangerService
          .changeFloorByName(payload.levelNumber)
          .then(() => {})
          .catch(() => {})
      }
    },
    async initPoiTypes({ commit, state, getters }) {
      const indoorviewStore = useIndoorviewStore()
      try {
        const [poiTypeGroups, poiTypes] = await Promise.all([
          indoorviewStore.ivApi.poi.poiTypeGroupRepository.findAll(),
          indoorviewStore.ivApi.poi.poiTypeRepository.findAll()
        ])
        const poiTypeOptions = []
        poiTypeGroups.forEach((group) => {
          const types = []
          poiTypes.forEach((type) => {
            if (type.poiTypeGroupId !== group.id) return
            // Alternative icon source if poi type._icon is undefined
            let iconSrc = iconMinus
            let instanceUrl = getters.navvisInstanceURL
            instanceUrl = instanceUrl.split('?')[0]
            if (type._icon) {
              iconSrc = type._icon?.includes('poi_navvis')
                ? instanceUrl + type._icon
                : instanceUrl + type._icon + '?x_authorization=' + state.navvisApiToken
            }
            types.push({
              id: type.id,
              name: type.name,
              visible: true,
              iconSrc
            })
          })
          poiTypeOptions.push({
            id: group.id,
            name: group.name,
            types,
            visible: true
          })
        })
        commit('setPoiTypesVisibility', poiTypeOptions)
      } catch (e) {
        console.error(e)
      }
    },
    [INIT_CONTEXTMENU]() {
      const indoorviewStore = useIndoorviewStore()
      // Removes the "Hide current location" from context menu
      const layerCameraImage = indoorviewStore.ivApi?.view?.service?.mainView?.layerCameraImage
      if (layerCameraImage?.onContextMenu) {
        layerCameraImage.onContextMenu = function () {
          return true
        }
      }
    },
    [LOAD_IV_COMPONENTS]({ commit, dispatch }) {
      const indoorviewStore = useIndoorviewStore()
      if (indoorviewStore.ivApi) {
        dispatch('initPoiTypes')
        commit('setIvLoaded', true)
        dispatch(INIT_CONTEXTMENU)

        // onFloorChange.connect omitted — fires on initial load and triggers an unwanted CHANGE_LEVEL
        if (indoorviewStore.ivApi.routing) {
          // Old instances (< 2.5) don't support routing
          return indoorviewStore.ivApi.routing.onRouteChanged.connect(function (route) {
            if (route) {
              commit(SET_ROUTE, { route: route })
            } else {
              commit(RESET_ROUTE)
            }
          })
        }
      } else {
        console.error('Indoorviewer must be set before initalizing its components')
      }
    },
    async [SET_IV_LEVELS]({ commit }, payload) {
      const indoorviewStore = useIndoorviewStore()
      let floors = []
      if (!payload.reset) {
        // Use provided floors or poll floorChangerService until available (replaces fixed 2s delay)
        floors = payload.floors ?? (await pollForFloors(indoorviewStore, payload.frame))
        if (floors?.length) {
          // Proceeding without panorama image check (pretending each level has panoramas).
          // The image-based check was removed due to performance issues; see git history for the
          // original implementation using getIndoorviewerImages + checkForPanoramaImages.
          floors.forEach((level) => {
            level.hasPanorama = true
          })
        }
      }
      commit(SET_IV_LEVELS_FOR_FRAME, {
        frame: payload.frame,
        floors: floors?.length ? floors : null
      })
    },
    [RESET_IVION_INSTANCES]({ commit }) {
      commit(CHANGE_PRIMARY_INSTANCE, null)
      commit(CHANGE_SECONDARY_INSTANCE, null)
      commit(SET_SPLIT_INDOORVIEWER, { splitIndoorViewer: false })
    },
    togglePoiGroup({ commit, state }, { id, visible }) {
      const indoorviewStore = useIndoorviewStore()
      const updatedPoiTypes = [...state.poiTypes]
      const poiGroup = updatedPoiTypes.find((group) => group.id === id)
      poiGroup.visible = visible
      poiGroup.types.forEach((type) => {
        type.visible = visible
        indoorviewStore.ivInstance?.legacyApi?.setPoiTypesVisibility(visible, [type.id])
      })
      indoorviewStore.ivInstance?.legacyApi?.setPoiTypesVisibility(visible, [id])
      commit('setPoiTypesVisibility', updatedPoiTypes)
    },
    togglePoiType({ commit, state }, { id, visible }) {
      const indoorviewStore = useIndoorviewStore()
      const updatedPoiTypes = [...state.poiTypes]
      updatedPoiTypes.some((group) => {
        const foundType = group.types.find((type) => type.id === id)
        if (foundType) {
          foundType.visible = visible
          group.visible = group.types.some((type) => type.visible)
          indoorviewStore.ivInstance?.legacyApi?.setPoiTypesVisibility(visible, [foundType.id])
          indoorviewStore.ivInstance?.legacyApi?.setPoiTypesVisibility(group.visible, [group.id])
          return
        }
      })
      commit('setPoiTypesVisibility', updatedPoiTypes)
    }
  }
}
